import { useEffect, useState, type CSSProperties, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import logo from '@/assets/logo.svg';
import { Icon } from '@/components/ui/Icon';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useNow } from '@/hooks/useNow';
import { formatDateLong, initialsFrom } from '@/lib/format';
import { routes } from '@/lib/routes';
import { OWNER10_MODULES, owner10RouteForKey } from '@/features/owner/lib/owner10Catalog';
import {
  ownerApi,
  type OwnerDashboard,
  type OwnerDashboardEvent,
} from '@/features/workspace/api';
import type { WorkspaceKpi } from '@/features/workspace/types';

const SECTIONS = OWNER10_MODULES;
const KPI_ACCENTS = ['#e0aa35', '#3fd7ff', '#ff6259', '#49ddb7'];

const EMPTY_DASHBOARD: OwnerDashboard = {
  requests: [],
  threads: [],
  events: [],
  event_days: [],
  notebook_stats: { active: 0, drafts: 0, starred: 0, today: 0 },
};

function ownerRouteFromPath(path: string): string {
  if (path.startsWith('/owner/')) {
    return `${routes.owner}${path.slice('/owner'.length)}`;
  }
  if (path.startsWith('/')) {
    return `${routes.owner}${path}`;
  }
  return owner10RouteForKey(path);
}

function eventIcon(event: OwnerDashboardEvent): string {
  if (/сроч/i.test(event.status)) return '✎';
  if (/уточн/i.test(event.status)) return '▣';
  if (/событи/i.test(event.status)) return '◷';
  return '◇';
}

const OvCal = ({ eventDays }: { eventDays: number[] }) => {
  const now = useNow();
  const year = now.getFullYear();
  const month = now.getMonth();
  const today = now.getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const startOffset = (firstDow + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const nums = Array.from({ length: 42 }, (_, i) => {
    const day = i - startOffset + 1;
    return day >= 1 && day <= daysInMonth ? day : '';
  });
  const highlighted = new Set(eventDays);

  return (
    <div className="ov-cal">
      {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((d) => (
        <b key={d}>{d}</b>
      ))}
      {nums.map((n, i) => (
        <button
          key={i}
          type="button"
          className={`${n === today ? 'sel' : ''} ${typeof n === 'number' && highlighted.has(n) ? 'event' : ''}`}
          tabIndex={n ? 0 : -1}
        >
          {n}
        </button>
      ))}
    </div>
  );
};

export const OwnerHomeCabinet = () => {
  const { user } = useAuth();
  const { locale } = useI18n();
  const navigate = useNavigate();
  const now = useNow();
  const displayName = user?.fullName || 'Владелец системы';
  const [openingKey, setOpeningKey] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<OwnerDashboard>(EMPTY_DASHBOARD);
  const [kpis, setKpis] = useState<WorkspaceKpi[]>([]);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([ownerApi.dashboard(), ownerApi.workspace()])
      .then(([dashboardData, workspaceData]) => {
        if (cancelled) return;
        setDashboard(dashboardData);
        setKpis(workspaceData.kpis);
      })
      .catch(() => {
        if (!cancelled) {
          setDashboard(EMPTY_DASHBOARD);
          setKpis([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openModule = (event: MouseEvent, key: string, to: string) => {
    event.preventDefault();
    if (openingKey) return;
    setOpeningKey(key);
    window.setTimeout(() => navigate(to), 180);
  };

  const { notebook_stats: notebookStats, threads, events, event_days: eventDays } = dashboard;
  const urgentCount = events.filter((item) => /сроч/i.test(item.status)).length;
  const nearestLabel = events[0]?.when_label ?? '—';
  const lastSavedLabel = notebookStats.today > 0 ? 'сегодня' : '—';

  return (
    <section className="dashboard ov-premium-owner" id="dashboard">
      <div className="ov-home-grid">
        <div className="ov-left-premium">
          <section className="ov-card ov-notebook" style={{ '--a': '#9e80ff' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Личное пространство</div>
              <div className="ov-title-row">
                <div>
                  <h3>Академический блокнот</h3>
                  <p>Источники, идеи, evidence и стратегические решения.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="document" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-metrics four">
                <div>
                  <small>Последнее сохранение</small>
                  <b>{lastSavedLabel}</b>
                </div>
                <div>
                  <small>Активные записи</small>
                  <b>{notebookStats.active}</b>
                </div>
                <div>
                  <small>Черновики</small>
                  <b>{notebookStats.drafts}</b>
                </div>
                <div>
                  <small>Избранное</small>
                  <b>
                    {notebookStats.starred}
                    {notebookStats.starred > 0 ? ' ★' : ''}
                  </b>
                </div>
              </div>
              <div className="ov-note-row">
                <strong>Стратегические заметки</strong>
                <span>Новая запись ещё не добавлена</span>
              </div>
              <div className="ov-note-row">
                <strong>Решения владельца</strong>
                <span>Новая запись ещё не добавлена</span>
              </div>
              <div className="ov-actions">
                <button type="button" onClick={() => navigate(`${routes.owner}/content`)}>
                  Открыть блокнот
                </button>
                <button type="button" onClick={() => navigate(`${routes.owner}/content`)}>
                  + Добавить запись
                </button>
              </div>
            </div>
          </section>

          <section className="ov-card ov-calendar" style={{ '--a': '#e2ad3a' } as CSSProperties}>
            <header>
              <div className="ov-kicker">{formatDateLong(now, locale)}</div>
              <div className="ov-title-row">
                <div>
                  <h3>Служебный календарь</h3>
                  <p>Сроки заявок, публикаций, проектов и встреч.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="calendar" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <OvCal eventDays={eventDays} />
              <div className="ov-metrics two">
                <div>
                  <small>Срочные события</small>
                  <b>{urgentCount}</b>
                </div>
                <div>
                  <small>Ближайший срок</small>
                  <b>{nearestLabel}</b>
                </div>
              </div>
              <div className="ov-actions">
                <button type="button" onClick={() => navigate(`${routes.owner}/meetings`)}>
                  Открыть календарь
                </button>
                <button type="button" onClick={() => navigate(`${routes.owner}/meetings`)}>
                  + Добавить событие
                </button>
              </div>
            </div>
          </section>

          <section className="ov-card" style={{ '--a': '#41d2ff' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Human review</div>
              <div className="ov-title-row">
                <div>
                  <h3>Вопрос–ответ</h3>
                  <p>Официальная база знаний и ваши вопросы специалистам.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="info" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <strong>База инструкций</strong>
                <span>Ответы проверяются человеком, не AI</span>
              </div>
              <div className="ov-actions single">
                <button type="button" onClick={() => navigate(routes.portalSection('instructions'))}>
                  Открыть Q&A
                </button>
              </div>
            </div>
          </section>

          <section className="ov-card" style={{ '--a': '#9e80ff' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Human support</div>
              <div className="ov-title-row">
                <div>
                  <h3>Служба поддержки</h3>
                  <p>Личный ticket, ответ и история обращений.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="chat" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <strong>Поддержка платформы</strong>
                <span>Обрабатывается службой поддержки, не AI</span>
              </div>
              <div className="ov-actions single">
                <button type="button" onClick={() => navigate(routes.portalSection('contacts'))}>
                  Открыть Support
                </button>
              </div>
            </div>
          </section>
        </div>

        <div className="ov-center-col">
          <div className="ov-owner-head-wrap">
            <section className="ov5-owner-hero">
              <div className="ov5-owner-title">
                <img className="ov5-owner-brand" src={logo} alt="ZERTTEU ACADEMY" />
                <div>
                  <h2>Кабинет владельца системы</h2>
                  <small>Полный системный контроль платформы</small>
                </div>
              </div>
              <div className="ov5-owner-persona">
                <div className="ov5-owner-photo fallback" aria-hidden="true">
                  {initialsFrom(displayName)}
                </div>
                <div className="ov5-owner-name">
                  <strong>{displayName}</strong>
                  <span className="ov5-role-chip">Владелец системы</span>
                  <p>
                    {user?.academiaId ? `Academia ID · ${user.academiaId}` : 'Zertteu Academy'}
                    {' · '}
                    <span className="ov5-online">Онлайн</span>
                  </p>
                </div>
                <div className="ov5-owner-fact">
                  <small>В системе с</small>
                  <b>01.01.2024</b>
                </div>
                <div className="ov5-owner-fact">
                  <small>Роль</small>
                  <b>Владелец системы</b>
                </div>
                <div className="ov5-owner-fact">
                  <small>Статус</small>
                  <b className="ov5-online">Онлайн</b>
                </div>
              </div>
              <div className="ov5-owner-kpis">
                {kpis.slice(0, 4).map((kpi, index) => (
                  <button
                    key={kpi.key}
                    type="button"
                    className="ov5-owner-kpi"
                    style={{ '--k': KPI_ACCENTS[index % KPI_ACCENTS.length] } as CSSProperties}
                    onClick={() => {
                      if (kpi.filter_route) navigate(owner10RouteForKey(kpi.filter_route));
                    }}
                  >
                    <span className="ov5-kpi-ico">▱</span>
                    <span>
                      <small>{kpi.label.ru}</small>
                      <b>{kpi.value}</b>
                    </span>
                    <span>›</span>
                  </button>
                ))}
              </div>
            </section>
          </div>

          <div className="owner-hub-top-actions">
            <button type="button" className="owner-hub-ai-action" onClick={() => navigate(routes.assistant)}>
              <span className="spark" aria-hidden="true">
                ✦
              </span>
              <span>
                <strong>+ Super AI</strong>
                <small>Научный онлайн-консультант</small>
              </span>
            </button>
            <button type="button" className="owner-hub-create" onClick={() => navigate(routes.newRequest)}>
              + Создать заявку
            </button>
          </div>

          <div className="owner-hub-sections-head">
            <div>
              <h3>Разделы</h3>
              <small>10 основных модулей кабинета владельца</small>
            </div>
          </div>

          <div className="owner-hub-grid">
            {SECTIONS.map((section) => (
              <Link
                key={section.key}
                to={section.to}
                className={`owner-hub-card${openingKey === section.key ? ' is-opening' : ''}`}
                style={{ '--action': section.accent } as CSSProperties}
                onClick={(event) => openModule(event, section.key, section.to)}
              >
                <span className="owner-hub-index">{section.num}</span>
                <span className="owner-hub-icon" aria-hidden="true">
                  <Icon name={section.icon} size={22} />
                </span>
                <span className="owner-hub-copy">
                  <strong>{section.title}</strong>
                  <span>{section.text}</span>
                </span>
                <span className="owner-hub-arrow" aria-hidden="true">
                  ›
                </span>
              </Link>
            ))}
          </div>

          <section
            className="ov-card ov-trajectory v17-center-trajectory"
            style={{ '--a': '#49ddb7' } as CSSProperties}
          >
            <header>
              <div className="ov-kicker">Scientific GPS</div>
              <div className="ov-title-row">
                <div>
                  <h3>Моя научная траектория</h3>
                  <p>Путь развития, публикационный pipeline и ключевые достижения.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="chart" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-track">
                <div className="done">
                  <i>✓</i>
                  <b>Старт</b>
                  <span>2024</span>
                </div>
                <div className="done">
                  <i>✓</i>
                  <b>Исследователь</b>
                  <span>2025</span>
                </div>
                <div className="now">
                  <i>◆</i>
                  <b>Лидер проекта</b>
                  <span>2026</span>
                </div>
                <div className="future">
                  <i>◇</i>
                  <b>Эксперт</b>
                  <span>2027</span>
                </div>
              </div>
              <details className="ov5-trajectory-insight">
                <summary>
                  <i>✦</i>
                  <span>
                    <strong>Следующий научный шаг</strong>
                    <small>Развернуть персональный mini-insight</small>
                  </span>
                  <span>›</span>
                </summary>
                <div className="ov5-insight-body">
                  <div>
                    <small>Publication pipeline</small>
                    <b>2 рукописи в работе</b>
                  </div>
                  <div>
                    <small>Next 90 Days</small>
                    <b>4 ключевых действия</b>
                  </div>
                  <div>
                    <small>Research focus</small>
                    <b>Лингвоконфликтология</b>
                  </div>
                  <div>
                    <small>Evidence gap</small>
                    <b>Нужно 3 новых источника</b>
                  </div>
                </div>
              </details>
              <div className="ov-actions single">
                <button type="button" onClick={() => navigate(routes.researcher)}>
                  Открыть траекторию
                </button>
              </div>
            </div>
          </section>
        </div>

        <div className="ov-right-premium">
          <section className="ov-card ov-events" style={{ '--a': '#ff6e56' } as CSSProperties}>
            <header>
              <div className="ov-kicker">Ближайшие действия</div>
              <div className="ov-title-row">
                <div>
                  <h3>Мероприятия и действия</h3>
                  <p>Срок, связь, приоритет и текущий статус каждой работы.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="trophy" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              {events.length ? (
                events.map((item) => (
                  <div
                    key={item.id}
                    className="ov-event"
                    style={{ '--ev': item.accent } as CSSProperties}
                  >
                    <span className="ov-ev-ico" aria-hidden="true">
                      {eventIcon(item)}
                    </span>
                    <time>{item.when_label}</time>
                    <strong>{item.title}</strong>
                    <small>{item.meta}</small>
                    <em className="ov-ev-status">{item.status}</em>
                    <button
                      type="button"
                      onClick={() => navigate(ownerRouteFromPath(item.to_path))}
                      aria-label="Открыть"
                    >
                      Открыть
                    </button>
                  </div>
                ))
              ) : (
                <div className="ov-note-row">
                  <span>Нет ближайших событий</span>
                </div>
              )}
              <button
                type="button"
                className="ea117-launch"
                onClick={() => navigate(`${routes.owner}/meetings`)}
              >
                Открыть все мероприятия →
              </button>
            </div>
          </section>

          <button
            type="button"
            className="ov-card ov-side-entry"
            style={{ '--a': '#41d2ff' } as CSSProperties}
            onClick={() => navigate(`${routes.owner}/communications`)}
            aria-label="Открыть академические связи"
          >
            <header>
              <div className="ov-kicker">Коммуникации</div>
              <div className="ov-title-row">
                <div>
                  <h3>Академические связи</h3>
                  <p>Контакты, диалоги и совместные материалы.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="chat" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <span>
                  {threads.length
                    ? `${threads.length} активных диалогов`
                    : 'Открыть полноценный раздел связей'}
                </span>
              </div>
              <span className="ea117-launch">Открыть все связи →</span>
            </div>
          </button>

          <button
            type="button"
            className="ov-card ov-side-entry"
            style={{ '--a': '#9e80ff' } as CSSProperties}
            onClick={() => navigate(`${routes.owner}/communications`)}
            aria-label="Открыть альянсы"
          >
            <header>
              <div className="ov-kicker">Академические группы</div>
              <div className="ov-title-row">
                <div>
                  <h3>Альянсы</h3>
                  <p>Группы, совместные проекты и научные сети.</p>
                </div>
                <span className="ov-icon" aria-hidden="true">
                  <Icon name="graduation" size={22} />
                </span>
              </div>
            </header>
            <div className="ov-body">
              <div className="ov-note-row">
                <span>Открыть полноценный раздел альянсов</span>
              </div>
              <span className="ea117-launch">Открыть все альянсы →</span>
            </div>
          </button>
        </div>
      </div>
    </section>
  );
};
